import { forwardRef, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Avatar,
  Badge,
  Paper,
  Timeline,
  Textarea,
  Tooltip,
  ThemeIcon,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconClock,
  IconUser,
  IconMessage,
  IconAlertCircle,
} from '@tabler/icons-react';
import { Button, Card } from '../../ui';
import { FadeIn, SlideIn } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface ApprovalStep {
  id: string;
  title: string;
  description?: string;
  approver?: {
    id: string;
    name: string;
    email?: string;
    avatar?: string;
    role?: string;
  };
  status: 'pending' | 'approved' | 'rejected' | 'skipped' | 'waiting';
  timestamp?: Date;
  comment?: string;
  order: number;
}

export interface ApprovalFlowProps {
  steps: ApprovalStep[];
  currentStep?: number;
  title?: string;
  description?: string;
  requestedBy?: {
    id: string;
    name: string;
    avatar?: string;
  };
  requestedAt?: Date;
  canApprove?: boolean;
  canReject?: boolean;
  onApprove?: (stepId: string, comment?: string) => void;
  onReject?: (stepId: string, comment?: string) => void;
  onRequestChanges?: (stepId: string, comment: string) => void;
  showCommentInput?: boolean;
  loading?: boolean;
  className?: string;
}

export const ApprovalFlow = forwardRef<HTMLDivElement, ApprovalFlowProps>(
  (
    {
      steps,
      currentStep,
      title,
      description,
      requestedBy,
      requestedAt,
      canApprove = false,
      canReject = false,
      onApprove,
      onReject,
      onRequestChanges,
      showCommentInput = true,
      loading = false,
      className,
    },
    ref
  ) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const sortedSteps = [...steps].sort((a, b) => a.order - b.order);
    const activeStepIndex = currentStep ?? sortedSteps.findIndex((s) => s.status === 'waiting');

    const getStatusIcon = (status: ApprovalStep['status']) => {
      switch (status) {
        case 'approved':
          return <IconCheck size={16} />;
        case 'rejected':
          return <IconX size={16} />;
        case 'pending':
        case 'waiting':
          return <IconClock size={16} />;
        case 'skipped':
          return <IconAlertCircle size={16} />;
        default:
          return <IconUser size={16} />;
      }
    };

    const getStatusColor = (status: ApprovalStep['status']) => {
      switch (status) {
        case 'approved':
          return 'green';
        case 'rejected':
          return 'red';
        case 'waiting':
          return 'blue';
        case 'skipped':
          return 'gray';
        default:
          return 'gray';
      }
    };

    const formatDate = (date?: Date) => {
      if (!date) return '';
      return new Date(date).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    return (
      <FadeIn>
        <Card ref={ref} className={className}>
          <Stack gap="lg">
            {/* Header */}
            {(title || requestedBy) && (
              <Box>
                {title && <Text size="lg" fw={600} style={accessibilityStyles}>{title}</Text>}
                {description && <Text size="sm" c="dimmed" style={accessibilityStyles}>{description}</Text>}
                {requestedBy && (
                  <Group gap="sm" mt="sm">
                    <Avatar src={requestedBy.avatar} size="sm" radius="xl">
                      {requestedBy.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Text size="sm" style={accessibilityStyles}>Requested by {requestedBy.name}</Text>
                      {requestedAt && (
                        <Text size="xs" c="dimmed" style={accessibilityStyles}>{formatDate(requestedAt)}</Text>
                      )}
                    </Box>
                  </Group>
                )}
              </Box>
            )}

            {/* Timeline */}
            <Timeline active={activeStepIndex} bulletSize={32} lineWidth={2}>
              {sortedSteps.map((step, index) => (
                <Timeline.Item
                  key={step.id}
                  bullet={
                    <ThemeIcon
                      size={32}
                      variant={step.status === 'waiting' ? 'filled' : 'light'}
                      color={getStatusColor(step.status)}
                      radius="xl"
                    >
                      {getStatusIcon(step.status)}
                    </ThemeIcon>
                  }
                  title={
                    <Group gap="sm">
                      <Text fw={500} style={accessibilityStyles}>{step.title}</Text>
                      <Badge size="sm" color={getStatusColor(step.status)} variant="light">
                        {step.status}
                      </Badge>
                    </Group>
                  }
                >
                  <SlideIn direction="left" delay={index * 50}>
                    <Stack gap="xs" mt="xs">
                      {step.description && (
                        <Text size="sm" c="dimmed" style={accessibilityStyles}>{step.description}</Text>
                      )}

                      {step.approver && (
                        <Group gap="sm">
                          <Avatar src={step.approver.avatar} size="sm" radius="xl">
                            {step.approver.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Text size="sm" style={accessibilityStyles}>{step.approver.name}</Text>
                            {step.approver.role && (
                              <Text size="xs" c="dimmed" style={accessibilityStyles}>{step.approver.role}</Text>
                            )}
                          </Box>
                          {step.timestamp && (
                            <Text size="xs" c="dimmed" ml="auto" style={accessibilityStyles}>
                              {formatDate(step.timestamp)}
                            </Text>
                          )}
                        </Group>
                      )}

                      {step.comment && (
                        <Paper p="sm" bg="gray.0" radius="sm">
                          <Group gap="xs" mb={4}>
                            <IconMessage size={14} />
                            <Text size="xs" fw={500} style={accessibilityStyles}>Comment</Text>
                          </Group>
                          <Text size="sm" style={accessibilityStyles}>{step.comment}</Text>
                        </Paper>
                      )}

                      {/* Action buttons for waiting step */}
                      {step.status === 'waiting' && (canApprove || canReject) && (
                        <ApprovalActions
                          stepId={step.id}
                          canApprove={canApprove}
                          canReject={canReject}
                          onApprove={onApprove}
                          onReject={onReject}
                          onRequestChanges={onRequestChanges}
                          showCommentInput={showCommentInput}
                          loading={loading}
                        />
                      )}
                    </Stack>
                  </SlideIn>
                </Timeline.Item>
              ))}
            </Timeline>
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

interface ApprovalActionsProps {
  stepId: string;
  canApprove: boolean;
  canReject: boolean;
  onApprove?: (stepId: string, comment?: string) => void;
  onReject?: (stepId: string, comment?: string) => void;
  onRequestChanges?: (stepId: string, comment: string) => void;
  showCommentInput: boolean;
  loading: boolean;
}

function ApprovalActions({
  stepId,
  canApprove,
  canReject,
  onApprove,
  onReject,
  onRequestChanges,
  showCommentInput,
  loading,
}: ApprovalActionsProps) {
  const [comment, setComment] = React.useState('');

  return (
    <Paper p="md" withBorder mt="sm">
      <Stack gap="sm">
        {showCommentInput && (
          <Textarea
            placeholder="Add a comment (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            minRows={2}
          />
        )}
        <Group gap="sm">
          {canApprove && onApprove && (
            <Button
              color="green"
              leftSection={<IconCheck size={16} />}
              onClick={() => onApprove(stepId, comment)}
              loading={loading}
            >
              Approve
            </Button>
          )}
          {canReject && onReject && (
            <Button
              color="red"
              variant="light"
              leftSection={<IconX size={16} />}
              onClick={() => onReject(stepId, comment)}
              loading={loading}
            >
              Reject
            </Button>
          )}
          {onRequestChanges && (
            <Button
              variant="subtle"
              onClick={() => comment && onRequestChanges(stepId, comment)}
              disabled={!comment}
              loading={loading}
            >
              Request Changes
            </Button>
          )}
        </Group>
      </Stack>
    </Paper>
  );
}

// Add React import for useState in ApprovalActions
import React from 'react';

ApprovalFlow.displayName = 'ApprovalFlow';

export default ApprovalFlow;
